<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  screens: { type: Array, required: true },
  activeScreenId: { type: String, default: '' },
});

const emit = defineEmits(['selectScreen']);

const { t } = useI18n();

const NODE_W = 120;
const NODE_H = 36;
const GAP_Y = 56;
const PAD_X = 16;
const PAD_Y = 12;

const nodes = computed(() =>
  props.screens.map((screen, i) => ({
    id: screen.id,
    title: screen.title,
    displayTitle:
      screen.title.length > 13
        ? `${screen.title.slice(0, 12)}\u2026`
        : screen.title,
    displayId:
      screen.id.length > 16 ? `${screen.id.slice(0, 15)}\u2026` : screen.id,
    terminal: screen.terminal,
    x: PAD_X,
    y: PAD_Y + i * GAP_Y,
    hasComplete: (screen.layout?.children || []).some(
      c => c.type === 'Footer' && c['on-click-action']?.name === 'complete'
    ),
  }))
);

const edges = computed(() => {
  const result = [];
  const idToIndex = {};
  props.screens.forEach((s, i) => {
    idToIndex[s.id] = i;
  });

  props.screens.forEach((screen, fromIdx) => {
    (screen.layout?.children || []).forEach(child => {
      const action = child['on-click-action'];
      if (!action || action.name !== 'navigate' || !action.next?.name) return;
      const targetId = action.next.name;
      const toIdx = idToIndex[targetId];
      if (toIdx === undefined) return;

      result.push({
        fromIdx,
        toIdx,
        fromId: screen.id,
        toId: targetId,
        label: child.label || '',
        type: child.type,
      });
    });
  });
  return result;
});

const svgWidth = computed(() => NODE_W + PAD_X * 2 + 30);
const svgHeight = computed(
  () => PAD_Y * 2 + Math.max(1, props.screens.length) * GAP_Y - (GAP_Y - NODE_H)
);

function edgePath(edge) {
  const fromY = PAD_Y + edge.fromIdx * GAP_Y + NODE_H / 2;
  const toY = PAD_Y + edge.toIdx * GAP_Y + NODE_H / 2;
  const rightX = PAD_X + NODE_W;
  const curveX = rightX + 18;

  return `M ${rightX} ${fromY} C ${curveX} ${fromY}, ${curveX} ${toY}, ${rightX} ${toY}`;
}

function selectNode(screenId) {
  const idx = props.screens.findIndex(s => s.id === screenId);
  if (idx >= 0) emit('selectScreen', idx);
}
</script>

<template>
  <div class="border-t border-n-weak">
    <div class="px-3 py-2">
      <span
        class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wide"
      >
        {{ t('WHATSAPP_FLOWS.NAVIGATION_GRAPH.TITLE') }}
      </span>
    </div>
    <div class="overflow-auto px-1 pb-2 max-h-[260px]">
      <svg :width="svgWidth" :height="svgHeight" class="block">
        <defs>
          <marker
            id="nav-arrow"
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
          >
            <path d="M 0 0 L 6 3 L 0 6 Z" class="fill-n-slate-9" />
          </marker>
          <marker
            id="nav-arrow-brand"
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
          >
            <path d="M 0 0 L 6 3 L 0 6 Z" class="fill-n-brand" />
          </marker>
        </defs>

        <!-- Edges -->
        <path
          v-for="(edge, idx) in edges"
          :key="`edge-${idx}`"
          :d="edgePath(edge)"
          fill="none"
          stroke-width="1.5"
          :class="[
            edge.fromId === activeScreenId || edge.toId === activeScreenId
              ? 'stroke-n-brand'
              : 'stroke-n-slate-7',
          ]"
          :marker-end="
            edge.fromId === activeScreenId || edge.toId === activeScreenId
              ? 'url(#nav-arrow-brand)'
              : 'url(#nav-arrow)'
          "
        />

        <!-- Nodes -->
        <g
          v-for="node in nodes"
          :key="node.id"
          class="cursor-pointer"
          @click="selectNode(node.id)"
        >
          <rect
            :x="node.x"
            :y="node.y"
            :width="NODE_W"
            :height="NODE_H"
            rx="6"
            ry="6"
            :class="[
              node.id === activeScreenId
                ? 'fill-n-brand-subtle stroke-n-brand'
                : 'fill-n-solid-2 stroke-n-weak',
            ]"
            stroke-width="1.5"
          />
          <!-- Title -->
          <text
            :x="node.x + 8"
            :y="node.y + 15"
            class="text-[10px] font-medium"
            :class="[
              node.id === activeScreenId
                ? 'fill-n-brand-dark'
                : 'fill-n-slate-12',
            ]"
          >
            <tspan>{{ node.displayTitle }}</tspan>
          </text>
          <!-- Screen ID -->
          <text
            :x="node.x + 8"
            :y="node.y + 27"
            class="text-[8px] font-mono"
            :class="[
              node.id === activeScreenId ? 'fill-n-brand' : 'fill-n-slate-9',
            ]"
          >
            {{ node.displayId }}
          </text>
          <!-- Complete indicator -->
          <circle
            v-if="node.hasComplete"
            :cx="node.x + NODE_W - 10"
            :cy="node.y + NODE_H / 2"
            r="4"
            class="fill-n-green-9"
          />
        </g>
      </svg>

      <!-- Legend -->
      <div
        v-if="screens.length > 0"
        class="flex items-center gap-3 px-3 pt-1 text-[9px] text-n-slate-9"
      >
        <span class="flex items-center gap-1">
          <span class="inline-block w-2 h-2 rounded-full bg-n-green-9" />
          {{ t('WHATSAPP_FLOWS.NAVIGATION_GRAPH.COMPLETE') }}
        </span>
        <span class="flex items-center gap-1">
          <span class="inline-block w-3 h-0.5 bg-n-slate-7" />
          {{ t('WHATSAPP_FLOWS.NAVIGATION_GRAPH.NAVIGATE') }}
        </span>
      </div>
    </div>
  </div>
</template>
